import { useState, useCallback, useMemo, useEffect } from 'react';

import {
  useGetBalanceSheet,
  BalanceSheetFilter,
  BalanceSheetApiResponse,
} from '../../api/accounts';
import { useAppContext } from '../../contexts/Storage';

interface UseBalanceSheetOptions {
  recordId?: number;
}

// Helper to get default date range (all-time — matches dashboard KPI cumulative balances)
const getDefaultDateRange = () => {
  const today = new Date();

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  return {
    start_date: `1970-01-01 00:00:00`,
    end_date: `${formatDate(today)} 23:59:59`,
  };
};

const toNumber = (val: unknown): number => {
  if (typeof val === 'number' && !Number.isNaN(val)) return val;
  if (typeof val === 'string' && val.trim() !== '' && !Number.isNaN(Number(val))) {
    return Number(val);
  }
  return 0;
};

// Types for mapped balance sheet data
export interface BalanceSheetItem {
  name: string;
  amount: number;
  isSubItem?: boolean;
}

export interface BalanceSheetSection {
  title: string;
  items: BalanceSheetItem[];
  subTotal?: number;
}

export interface MappedBalanceSheetData {
  debitSide: {
    sections: BalanceSheetSection[];
    total: number;
  };
  creditSide: {
    sections: BalanceSheetSection[];
    total: number;
  };
}

const useBalanceSheet = (options: UseBalanceSheetOptions = {}) => {
  const { recordId: initialRecordId } = options;
  const { user, selectedRecord } = useAppContext();

  // Use provided recordId or fall back to selected record
  const recordId = initialRecordId ?? selectedRecord?.id;
  const userId = user?.id;

  // Get default date range
  const defaultDates = getDefaultDateRange();

  // Filter state - include default dates as API requires them
  const [filters, setFilters] = useState<Partial<BalanceSheetFilter>>({
    start_date: defaultDates.start_date,
    end_date: defaultDates.end_date,
  });

  // API hook - only fetch when we have required params
  const hasRequiredParams = !!recordId && !!userId && !!filters.start_date && !!filters.end_date;
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetBalanceSheet({
    filters: {
      ...filters,
      record_id: recordId,
      user_id: userId,
    },
    enabled: hasRequiredParams,
  });

  // Raw data
  const rawData = useMemo(() => data?.data, [data?.data]);

  // Refetch when books change elsewhere (transactions, AI, etc.)
  useEffect(() => {
    const onBooksUpdated = () => {
      void refetch();
    };
    window.addEventListener('balancio-books-updated', onBooksUpdated);
    return () => window.removeEventListener('balancio-books-updated', onBooksUpdated);
  }, [refetch]);

  // Helper to capitalize first letter
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  // Map API response to display format
  const mappedData = useMemo((): MappedBalanceSheetData | null => {
    if (!rawData) return null;

    const response = rawData as BalanceSheetApiResponse;

    // Process Assets (Debit Side) — totals must match the lines shown
    const debitSections: BalanceSheetSection[] = [];
    let debitTotal = 0;

    if (response.assets) {
      const assetsSection: BalanceSheetSection = {
        title: 'Assets:',
        items: [],
      };

      Object.entries(response.assets).forEach(([key, value]) => {
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          assetsSection.items.push({ name: `${capitalize(key)}:`, amount: 0 });

          Object.entries(value as Record<string, unknown>).forEach(([itemName, itemAmount]) => {
            const amount = toNumber(itemAmount);
            assetsSection.items.push({ name: itemName, amount, isSubItem: true });
            debitTotal += amount;
          });
        } else {
          const amount = toNumber(value);
          assetsSection.items.push({ name: capitalize(key), amount });
          debitTotal += amount;
        }
      });

      debitSections.push(assetsSection);
    }

    // Process Liabilities (Credit Side)
    const creditSections: BalanceSheetSection[] = [];
    let liabilitiesTotal = 0;

    if (response.liabilities) {
      const liabilitiesSection: BalanceSheetSection = {
        title: 'Liabilities:',
        items: [],
      };

      Object.entries(response.liabilities).forEach(([name, amount]) => {
        const value = toNumber(amount);
        liabilitiesSection.items.push({ name, amount: value, isSubItem: true });
        liabilitiesTotal += value;
      });

      creditSections.push(liabilitiesSection);
    }

    // Capital = Assets − Liabilities (same equation as the API).
    // Derive from the mapped lines so DR/CR totals match what is listed.
    const capitalAmount = debitTotal - liabilitiesTotal;
    creditSections.push({
      title: '',
      items: [{ name: 'Capital', amount: capitalAmount }],
    });

    return {
      debitSide: {
        sections: debitSections,
        total: debitTotal,
      },
      creditSide: {
        sections: creditSections,
        total: liabilitiesTotal + capitalAmount,
      },
    };
  }, [rawData]);

  // Filter handlers
  const updateFilter = useCallback((updates: Partial<BalanceSheetFilter>) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleDateFilter = useCallback((startDate?: string, endDate?: string) => {
    const defaults = getDefaultDateRange();
    updateFilter({
      start_date: startDate ? `${startDate} 00:00:00` : defaults.start_date,
      end_date: endDate ? `${endDate} 23:59:59` : defaults.end_date,
    });
  }, [updateFilter]);

  const resetFilters = useCallback(() => {
    const defaultDates = getDefaultDateRange();
    setFilters({
      start_date: defaultDates.start_date,
      end_date: defaultDates.end_date,
    });
  }, []);

  return {
    // Data
    rawData,
    mappedData,

    // Loading states
    isLoading,
    hasRequiredParams,

    // Error state
    error,

    // Filter state and handlers
    filters,
    handleDateFilter,
    resetFilters,
    updateFilter,

    // Refetch
    refetch,
  };
};

export default useBalanceSheet;
